import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { QaiDialogComponent, QaiTabDirective, QaiTabsComponent } from '@qai/ui';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ProcessMarkdownPipe } from 'src/app/pipes/process-markdown.pipe';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AuthorReactiveFormService } from '../../../services/author-reactive-form.service';
import { AbstractControl, FormGroup } from '@angular/forms';

@Component({
  selector: 'preview-simulations-details-modal',
  standalone: true,
  templateUrl: './preview-simulation-modal.component.html',
  styleUrls: ['./preview-simulation-modal.component.scss'],
  imports: [
    CommonModule,
    QaiDialogComponent,
    QaiTabsComponent,
    QaiTabDirective,
    ProcessMarkdownPipe,
  ],
})
export class PreviewSimulationDetailsModalComponent
  implements AfterViewInit, OnDestroy
{
  @ViewChild('dialog') dialogRef: QaiDialogComponent | undefined;
  @ViewChild('dialog', { read: ElementRef }) dialogElementRef!: ElementRef;
  @Input('open') open: boolean = false;
  @Input() tabs: { label: string; content: string }[] = [];

  @Output() closeModal = new EventEmitter<void>();

  form!: FormGroup;
  callUuid: string = '';
  simulationName: string = '';

  contentMaxHeight: string = '';
  private resizeObserver?: ResizeObserver;

  private readonly MODAL_HEIGHT_CONFIG = {
    totalModalHeightVh: 75,
    innerPaddingVh: 4,
    headerMarginBottomVh: 2,
    tabsHeightVh: 4,
    contentMarginVh: 2,
    minContentHeightVh: 20,
    defaultHeadingHeightPx: 60,
  };

  constructor(
    private authorReactiveFormService: AuthorReactiveFormService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;

    this.authorReactiveFormService.getForm(this.callUuid).subscribe((form) => {
      this.initializeForm(form);
    });
  }

  private initializeForm(form: FormGroup): void {
    this.form = form.get(SimulationConstants.GENERAL_SECTION) as FormGroup;

    const simulationNameControl = this.form.get('simulationName');
    this.simulationName = simulationNameControl?.value || '';

    if (simulationNameControl) {
      this.subscribeToSimulationNameChanges(simulationNameControl);
    }
  }

  private subscribeToSimulationNameChanges(control: AbstractControl): void {
    control.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged()
      )
      .subscribe((updatedName) => {
        this.simulationName = updatedName;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open']?.currentValue) {
      this.showModal();
    }
  }

  handleClose() {
    this.closeModal.emit();
  }

  hideModal() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  showModal() {
    if (this.dialogRef) {
      this.dialogRef.showModal();
      setTimeout(() => this.calculateContentHeight(), 100);
    }
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateContentHeight();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private calculateContentHeight(): void {
    if (!this.dialogElementRef?.nativeElement) {
      return;
    }

    const dialogElement = this.dialogElementRef.nativeElement;

    let headingElement = null;

    if (!headingElement) {
      const allElements = dialogElement.querySelectorAll('*');
      for (const element of allElements) {
        if (element.textContent?.trim() === this.simulationName?.trim()) {
          headingElement = element;
          break;
        }
      }
    }

    const headingHeightPx = headingElement ? headingElement.offsetHeight : 60;

    const pixelsIn1Vh = 10.8;
    const headingHeightVh = Math.ceil(headingHeightPx / pixelsIn1Vh);

    const totalFixedHeightVh =
      this.MODAL_HEIGHT_CONFIG.innerPaddingVh +
      this.MODAL_HEIGHT_CONFIG.headerMarginBottomVh +
      this.MODAL_HEIGHT_CONFIG.tabsHeightVh +
      this.MODAL_HEIGHT_CONFIG.contentMarginVh;

    const availableHeightVh =
      this.MODAL_HEIGHT_CONFIG.totalModalHeightVh -
      headingHeightVh -
      totalFixedHeightVh;

    const finalHeightVh = Math.max(availableHeightVh, 20);

    this.contentMaxHeight = `${finalHeightVh}vh`;
  }
}
